import React, { useState, useEffect } from 'react';
import { Plus, X, Save, Trash2, Briefcase, Building2, UserCheck, Calculator, FileText } from 'lucide-react';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const blankForm = () => ({
  empresa: '', cnpj_cpf: '', categoria: '', numero_controle: '',
  data_agendamento: '', hora: '',
  taxa: '', dev_documentacao: 'Não', remarcada: 'Não', senha_hora: '',
  encaixe_senha: '', proc_empresa: 0, total_proc: 0,
  trabalhador_nome: '', trabalhador_cpf: '', trabalhador_ctps: '',
  data_nascimento: '', data_admissao: '', data_demissao: '',
  motivo_demissao: 'Sem justa causa', funcao: '',
  salario_base: 0, saldo_fgts: 0, aviso_tipo: 'Indenizado',
  ferias_vencidas: false,
  status: 'Em andamento',
});

// ── Cálculo de verbas rescisórias (estimativa CLT) ──────────────────────
function mesesEntre(d1, d2) {
  if (!d1 || !d2) return 0;
  const a = new Date(d1), b = new Date(d2);
  let meses = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() >= 15) meses += 1;
  return Math.max(meses, 0);
}

function calcInss(base) {
  // Tabela progressiva simplificada (faixas mensais)
  const faixas = [
    { limite: 1412.00, aliq: 0.075, deduz: 0 },
    { limite: 2666.68, aliq: 0.09, deduz: 21.18 },
    { limite: 4000.03, aliq: 0.12, deduz: 101.18 },
    { limite: 7786.02, aliq: 0.14, deduz: 181.18 },
  ];
  const faixa = faixas.find(f => base <= f.limite) || faixas[faixas.length - 1];
  return Math.max(base * faixa.aliq - faixa.deduz, 0);
}

function calcIrrf(baseAposInss) {
  if (baseAposInss <= 2259.20) return 0;
  if (baseAposInss <= 2826.65) return baseAposInss * 0.075 - 169.44;
  if (baseAposInss <= 3751.05) return baseAposInss * 0.15 - 381.44;
  if (baseAposInss <= 4664.68) return baseAposInss * 0.225 - 662.77;
  return baseAposInss * 0.275 - 896.00;
}

function calcularVerbas(f) {
  const salario = Number(f.salario_base) || 0;
  const justaCausa = f.motivo_demissao === 'Justa causa';
  const pedidoDemissao = f.motivo_demissao === 'Pedido de demissão';
  const demissao = f.data_demissao ? new Date(f.data_demissao) : null;
  const admissao = f.data_admissao ? new Date(f.data_admissao) : null;

  const diaDemissao = demissao ? demissao.getDate() : 0;
  const saldoSalario = (salario / 30) * diaDemissao;

  const anosTrabalhados = admissao && demissao ? Math.floor(mesesEntre(admissao, demissao) / 12) : 0;
  const diasAviso = Math.min(30 + anosTrabalhados * 3, 90);
  const avisoPrevio = (!justaCausa && !pedidoDemissao && f.aviso_tipo === 'Indenizado')
    ? (salario / 30) * diasAviso : 0;

  const mesesAnoDemissao = demissao ? demissao.getMonth() + (demissao.getDate() >= 15 ? 1 : 0) : 0;
  const decimoTerceiro = justaCausa ? 0 : (salario / 12) * mesesAnoDemissao;

  const mesesProporcionais = admissao && demissao ? Math.min(mesesEntre(admissao, demissao) % 12 || 12, 12) : 0;
  const feriasProporcionais = justaCausa ? 0 : (salario / 12) * mesesProporcionais;
  const tercoFeriasProp = feriasProporcionais / 3;

  const feriasVencidas = (!justaCausa && f.ferias_vencidas) ? salario : 0;
  const tercoFeriasVencidas = feriasVencidas / 3;

  const saldoFgts = Number(f.saldo_fgts) || 0;
  const multaFgts = (!justaCausa && !pedidoDemissao) ? saldoFgts * 0.4 : 0;

  const baseInss = saldoSalario + decimoTerceiro;
  const inss = calcInss(baseInss);
  const irrf = calcIrrf(baseInss - inss);

  const totalVerbas = saldoSalario + avisoPrevio + decimoTerceiro + feriasProporcionais + tercoFeriasProp + feriasVencidas + tercoFeriasVencidas;
  const totalDescontos = inss + irrf;
  const totalLiquido = totalVerbas - totalDescontos;

  return {
    saldoSalario, avisoPrevio, diasAviso, decimoTerceiro, mesesAnoDemissao,
    feriasProporcionais, tercoFeriasProp, mesesProporcionais,
    feriasVencidas, tercoFeriasVencidas, saldoFgts, multaFgts,
    inss, irrf, totalVerbas, totalDescontos, totalLiquido,
  };
}

const HomologacaoModule = ({ darkMode }) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('lista'); // 'lista' | 'form'
  const [tab, setTab] = useState('gerais');
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const verbas = calcularVerbas(form);

  const card = darkMode ? 'bg-[#161b27] border border-[#1e293b]' : 'bg-white border border-slate-200';
  const input = darkMode
    ? 'bg-[#1e2537] border border-[#2d3748] text-slate-200 placeholder-slate-500'
    : 'bg-slate-50 border border-slate-200 text-slate-800';
  const label = darkMode ? 'text-slate-400' : 'text-slate-500';
  const txt = darkMode ? 'text-slate-200' : 'text-slate-800';
  const sub = darkMode ? 'text-slate-500' : 'text-slate-400';

  const carregar = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/homologacoes`);
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar homologações:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const novo = () => { setForm(blankForm()); setEditId(null); setTab('gerais'); setView('form'); };

  const editar = (h) => {
    setForm({ ...blankForm(), ...h, ferias_vencidas: !!(h.verbas && h.verbas._feriasVencidas) });
    setEditId(h.id);
    setTab('gerais');
    setView('form');
  };

  const salvar = async () => {
    if (!form.empresa) { alert('Informe a empresa/razão social.'); setTab('gerais'); return; }
    const payload = {
      ...form,
      verbas: { ...verbas, _feriasVencidas: form.ferias_vencidas },
      total_proventos: verbas.totalVerbas,
      total_descontos: verbas.totalDescontos,
      total_liquido: verbas.totalLiquido,
    };
    try {
      if (editId) {
        await fetch(`${apiBaseUrl}/api/homologacoes/${editId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      } else {
        await fetch(`${apiBaseUrl}/api/homologacoes`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      }
      setView('lista');
      carregar();
    } catch (e) {
      console.error('Erro ao salvar homologação:', e);
      alert('Erro ao salvar. Veja o console.');
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta homologação?')) return;
    await fetch(`${apiBaseUrl}/api/homologacoes/${id}`, { method: 'DELETE' });
    carregar();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputCls = `w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`;
  const labelCls = `text-xs font-bold uppercase tracking-wider ${label}`;

  const Field = ({ lbl, children }) => (
    <div className="space-y-1">
      <label className={labelCls}>{lbl}</label>
      {children}
    </div>
  );

  if (view === 'lista') {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-black tracking-tight ${txt}`}>Homologações</h2>
            <p className={`text-sm font-medium ${sub}`}>Gerencie rescisões e cálculos de homologação.</p>
          </div>
          <button onClick={novo} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md transition-all">
            <Plus size={18} /> Nova Homologação
          </button>
        </div>

        <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${sub}`}>Carregando...</div>
          ) : lista.length === 0 ? (
            <div className={`p-10 text-center text-sm ${sub}`}>
              <Briefcase size={36} className="mx-auto mb-3 opacity-30" />
              <p>Nenhuma homologação encontrada.</p>
              <p className="text-xs mt-1">Comece criando uma nova homologação para realizar cálculos rescisórios.</p>
            </div>
          ) : (
            lista.map((h, i) => (
              <div key={h.id} onClick={() => editar(h)}
                className={`flex items-center px-6 py-4 cursor-pointer ${i < lista.length - 1 ? `border-b ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}` : ''} transition-colors ${darkMode ? 'hover:bg-[#1e293b]' : 'hover:bg-slate-50'}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mr-4 bg-blue-500/10">
                  <Briefcase size={18} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${txt}`}>{h.empresa} — {h.trabalhador_nome || 'sem trabalhador'}</p>
                  <p className={`text-xs mt-0.5 ${sub}`}>{h.categoria || 'Sem categoria'} · {h.data_agendamento || 'sem data'} · {h.status}</p>
                </div>
                <p className={`text-sm font-black mr-4 ${txt}`}>{fmt(h.total_liquido)}</p>
                <button onClick={(e) => { e.stopPropagation(); excluir(h.id); }}
                  className={`p-2 rounded-lg transition-all ${darkMode ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10' : 'text-slate-300 hover:text-red-500 hover:bg-red-50'}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'gerais', label: 'Dados Gerais', icon: <Building2 size={15} /> },
    { id: 'trabalhador', label: 'Trabalhador', icon: <UserCheck size={15} /> },
    { id: 'calculo', label: 'Cálculo', icon: <Calculator size={15} /> },
    { id: 'verbas', label: 'Verbas', icon: <FileText size={15} /> },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className={`${card} rounded-2xl p-6 shadow-sm flex items-center justify-between`}>
        <div>
          <h2 className={`text-2xl font-black tracking-tight ${txt}`}>{editId ? 'Editar Homologação' : 'Nova Homologação'}</h2>
          <p className={`text-sm font-medium ${sub}`}>Preencha os dados e realize os cálculos rescisórios.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setView('lista')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${darkMode ? 'border-[#2d3748] text-slate-300 hover:bg-[#1e293b]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <X size={16} /> Cancelar
          </button>
          <button onClick={salvar} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md transition-all">
            <Save size={16} /> Salvar Dados
          </button>
        </div>
      </div>

      <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
        <div className={`flex border-b ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}`}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-bold transition-all ${tab === t.id ? 'text-blue-600 border-b-2 border-blue-600' : `${sub} hover:${txt}`}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {tab === 'gerais' && (
            <>
              <h3 className={`text-base font-bold flex items-center gap-2 ${txt}`}><Building2 size={16} className="text-blue-500" /> Empresa e Agendamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field lbl="Empresa / Razão"><input className={inputCls} value={form.empresa} onChange={e => set('empresa', e.target.value)} /></Field>
                <Field lbl="CNPJ / CPF"><input className={inputCls} value={form.cnpj_cpf} onChange={e => set('cnpj_cpf', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field lbl="Categoria"><input className={inputCls} value={form.categoria} onChange={e => set('categoria', e.target.value)} /></Field>
                <Field lbl="Nº Controle"><input className={inputCls} value={form.numero_controle} onChange={e => set('numero_controle', e.target.value)} /></Field>
                <Field lbl="Data Agend."><input type="date" className={inputCls} value={form.data_agendamento} onChange={e => set('data_agendamento', e.target.value)} /></Field>
                <Field lbl="Hora"><input type="time" className={inputCls} value={form.hora} onChange={e => set('hora', e.target.value)} /></Field>
              </div>
              <h3 className={`text-base font-bold flex items-center gap-2 ${txt}`}><FileText size={16} className="text-emerald-500" /> Processo e Taxas</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field lbl="Taxa"><input className={inputCls} placeholder="Sim / Não / NT" value={form.taxa} onChange={e => set('taxa', e.target.value)} /></Field>
                <Field lbl="Dev. Documentação">
                  <select className={inputCls} value={form.dev_documentacao} onChange={e => set('dev_documentacao', e.target.value)}>
                    <option>Não</option><option>Sim</option>
                  </select>
                </Field>
                <Field lbl="Remarcada">
                  <select className={inputCls} value={form.remarcada} onChange={e => set('remarcada', e.target.value)}>
                    <option>Não</option><option>Sim</option>
                  </select>
                </Field>
                <Field lbl="Senha / Hora"><input className={inputCls} value={form.senha_hora} onChange={e => set('senha_hora', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field lbl="Encaixe Senha"><input className={inputCls} value={form.encaixe_senha} onChange={e => set('encaixe_senha', e.target.value)} /></Field>
                <Field lbl="Proc. Empresa"><input type="number" className={inputCls} value={form.proc_empresa} onChange={e => set('proc_empresa', e.target.value)} /></Field>
                <Field lbl="Total Proc."><input type="number" className={inputCls} value={form.total_proc} onChange={e => set('total_proc', e.target.value)} /></Field>
              </div>
            </>
          )}

          {tab === 'trabalhador' && (
            <>
              <h3 className={`text-base font-bold flex items-center gap-2 ${txt}`}><UserCheck size={16} className="text-blue-500" /> Identificação do Trabalhador</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field lbl="Nome"><input className={inputCls} value={form.trabalhador_nome} onChange={e => set('trabalhador_nome', e.target.value)} /></Field>
                <Field lbl="CPF"><input className={inputCls} value={form.trabalhador_cpf} onChange={e => set('trabalhador_cpf', e.target.value)} /></Field>
                <Field lbl="CTPS"><input className={inputCls} value={form.trabalhador_ctps} onChange={e => set('trabalhador_ctps', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Field lbl="Data de Nascimento"><input type="date" className={inputCls} value={form.data_nascimento} onChange={e => set('data_nascimento', e.target.value)} /></Field>
                <Field lbl="Função"><input className={inputCls} value={form.funcao} onChange={e => set('funcao', e.target.value)} /></Field>
                <Field lbl="Data de Admissão"><input type="date" className={inputCls} value={form.data_admissao} onChange={e => set('data_admissao', e.target.value)} /></Field>
                <Field lbl="Data de Demissão"><input type="date" className={inputCls} value={form.data_demissao} onChange={e => set('data_demissao', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field lbl="Motivo da Demissão">
                  <select className={inputCls} value={form.motivo_demissao} onChange={e => set('motivo_demissao', e.target.value)}>
                    <option>Sem justa causa</option>
                    <option>Pedido de demissão</option>
                    <option>Justa causa</option>
                    <option>Acordo (art. 484-A)</option>
                  </select>
                </Field>
                <Field lbl="Aviso Prévio">
                  <select className={inputCls} value={form.aviso_tipo} onChange={e => set('aviso_tipo', e.target.value)}>
                    <option>Indenizado</option><option>Trabalhado</option><option>Não se aplica</option>
                  </select>
                </Field>
                <Field lbl="Salário Base (R$)"><input type="number" step="0.01" className={inputCls} value={form.salario_base} onChange={e => set('salario_base', e.target.value)} /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <Field lbl="Saldo de FGTS (R$)"><input type="number" step="0.01" className={inputCls} value={form.saldo_fgts} onChange={e => set('saldo_fgts', e.target.value)} /></Field>
                <label className="flex items-center gap-2 pb-3 cursor-pointer">
                  <input type="checkbox" checked={form.ferias_vencidas} onChange={e => set('ferias_vencidas', e.target.checked)} />
                  <span className={`text-sm font-medium ${txt}`}>Possui férias vencidas não gozadas</span>
                </label>
              </div>
            </>
          )}

          {tab === 'calculo' && (
            <>
              <h3 className={`text-base font-bold flex items-center gap-2 ${txt}`}><Calculator size={16} className="text-blue-500" /> Resumo do Cálculo (estimativa CLT)</h3>
              <p className={`text-xs ${sub}`}>Valores calculados automaticamente a partir das datas, salário e motivo informados na aba Trabalhador. Conferir antes de homologar.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  ['Dias de aviso prévio considerados', verbas.diasAviso + ' dias'],
                  ['Meses p/ 13º proporcional', verbas.mesesAnoDemissao + ' meses'],
                  ['Meses p/ férias proporcionais', verbas.mesesProporcionais + ' meses'],
                  ['Base de cálculo INSS', fmt(verbas.saldoSalario + verbas.decimoTerceiro)],
                ].map(([k, v]) => (
                  <div key={k} className={`p-4 rounded-xl ${input}`}>
                    <p className={`text-xs font-bold uppercase ${label}`}>{k}</p>
                    <p className={`text-sm font-bold mt-1 ${txt}`}>{v}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'verbas' && (
            <>
              <h3 className={`text-base font-bold flex items-center gap-2 ${txt}`}><FileText size={16} className="text-emerald-500" /> Verbas Rescisórias</h3>
              <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}`}>
                {[
                  ['Saldo de salário', verbas.saldoSalario],
                  ['Aviso prévio indenizado', verbas.avisoPrevio],
                  ['13º salário proporcional', verbas.decimoTerceiro],
                  ['Férias proporcionais', verbas.feriasProporcionais],
                  ['1/3 férias proporcionais', verbas.tercoFeriasProp],
                  ['Férias vencidas', verbas.feriasVencidas],
                  ['1/3 férias vencidas', verbas.tercoFeriasVencidas],
                ].map(([k, v], i) => (
                  <div key={k} className={`flex justify-between px-4 py-3 text-sm ${i % 2 ? (darkMode ? 'bg-[#1e2537]/40' : 'bg-slate-50') : ''}`}>
                    <span className={sub}>{k}</span><span className={`font-bold ${txt}`}>{fmt(v)}</span>
                  </div>
                ))}
                <div className={`flex justify-between px-4 py-3 text-sm border-t ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}`}>
                  <span className={`font-bold ${txt}`}>Total de proventos</span><span className="font-black text-emerald-500">{fmt(verbas.totalVerbas)}</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className={sub}>(-) INSS</span><span className="font-bold text-red-500">{fmt(verbas.inss)}</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className={sub}>(-) IRRF</span><span className="font-bold text-red-500">{fmt(verbas.irrf)}</span>
                </div>
                <div className={`flex justify-between px-4 py-4 text-base border-t-2 ${darkMode ? 'border-[#1e293b] bg-[#1e2537]/60' : 'border-slate-200 bg-slate-50'}`}>
                  <span className={`font-black ${txt}`}>Total Líquido a Receber</span><span className="font-black text-blue-500">{fmt(verbas.totalLiquido)}</span>
                </div>
                <div className={`flex justify-between px-4 py-3 text-sm border-t ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}`}>
                  <span className={sub}>FGTS depositado (saldo informado)</span><span className={`font-bold ${txt}`}>{fmt(verbas.saldoFgts)}</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className={sub}>Multa de 40% sobre FGTS</span><span className="font-bold text-emerald-500">{fmt(verbas.multaFgts)}</span>
                </div>
              </div>
              <p className={`text-xs ${sub}`}>* INSS e IRRF calculados por tabela progressiva simplificada sobre saldo de salário + 13º proporcional. Verbas indenizatórias (aviso, férias) são isentas. Conferir valores oficiais antes da homologação.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomologacaoModule;
